import { Howl } from 'howler';
import { create } from 'zustand';

const musicModules = import.meta.glob('/assets/bgms/season2/*.mp3', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

export type MusicTrack = {
  title: string;
  src: string;
};

const formatTitle = (originalPath: string) => {
  return decodeURIComponent(originalPath.split('/').pop() || '').replace(
    /\.[^/.]+$/,
    '',
  );
};

export const allTracks: MusicTrack[] = Object.entries(musicModules)
  .filter(([, src]) => typeof src === 'string')
  .map(([originalPath, src]) => ({
    title: formatTitle(originalPath),
    src: src as string,
  }));

function shuffle<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

const calculateVolume = (volume: number) => {
  return volume * 0.01;
};

const createHowl = ({
  path,
  volume,
  playNext,
}: {
  path: string;
  volume: number;
  playNext: () => void;
}) => {
  return new Howl({
    src: [path],
    volume: calculateVolume(volume),
    onend: () => playNext(),
    onloaderror: () => {
      // #FIXME: sentry
      console.error('onloaderror');
      return;
    },
    onplayerror: () => {
      // #FIXME: sentry
      console.error('');
      return;
    },
  });
};

const loadCheckedTitles = (): string[] => {
  try {
    const raw = localStorage.getItem('bgmCheckedTitles');
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) {
      return parsed.filter((title) => allTracks.some((t) => t.title === title));
    }
    return [];
  } catch {
    return [];
  }
};

const buildPlaylist = (checkedTitles: string[]): MusicTrack[] => {
  if (checkedTitles.length === 0) {
    return shuffle([...allTracks]);
  }
  return allTracks.filter((t) => checkedTitles.includes(t.title));
};

type BgmState = {
  playlist: MusicTrack[];
  currentIndex: number;
  volume: number;
  currentBgm: Howl | null;
  currentBgmTitle: string;
  isInitialized: boolean;
  isBgmEnabled: boolean;
  checkedTitles: string[];
  isCurrentlyPlaying: boolean;
  init: () => void;
  play: (fadeInDuration?: number) => void;
  playNext: () => void;
  pause: () => void;
  resume: () => void;
  setVolume: (v: number) => void;
  toggleBgm: () => void;
  toggleTrack: (title: string) => void;
  setCheckedTitles: (titles: string[]) => void;
  applyChecklist: () => void;
  cancelFadeOut: () => void;
  fadeIn: (duration: number) => void;
  isPlaying: () => boolean;
  destroy: () => void;
};

export const useBgmStore = create<BgmState>((set, get) => ({
  playlist: [],
  isInitialized: false,
  currentIndex: 0,
  currentBgmTitle: '',
  volume: parseInt(localStorage.getItem('bgmVolume') || '25', 10),
  isBgmEnabled: localStorage.getItem('isBgmEnabled') !== 'false',
  currentBgm: null,
  fadeOutTimer: null,
  checkedTitles: loadCheckedTitles(),
  isCurrentlyPlaying: false,

  init: () => {
    const { play, checkedTitles } = get();
    set({ isInitialized: true });
    set({ playlist: buildPlaylist(checkedTitles) });
    set({ currentIndex: 0 });
    play();
  },

  play: (fadeInDuration?: number) => {
    const {
      playlist,
      currentBgm,
      init,
      fadeIn,
      isInitialized,
      isBgmEnabled,
      currentIndex,
      volume,
      playNext,
      cancelFadeOut,
    } = get();

    cancelFadeOut();

    if (!isInitialized) {
      init();
    }

    if (!isBgmEnabled) {
      return;
    }

    if (playlist.length === 0) {
      return;
    }

    if (currentBgm?.playing()) {
      return;
    }

    if (fadeInDuration && fadeInDuration > 0) {
      fadeIn(fadeInDuration);
      return;
    }

    const { currentBgm: stale } = get();
    if (stale) {
      stale.unload();
      set({ currentBgm: null });
    }

    const track = playlist[currentIndex];
    const newSound = createHowl({
      path: track.src,
      volume,
      playNext,
    });
    set({
      currentIndex,
      currentBgm: newSound,
      currentBgmTitle: track.title,
      isCurrentlyPlaying: true,
    });
    newSound?.play();
  },

  playNext: () => {
    const { currentIndex, playlist, currentBgm, play } = get();
    const nextIndex = (currentIndex + 1) % playlist.length;
    currentBgm?.unload();
    set({ currentIndex: nextIndex, currentBgm: null });
    play();
  },

  pause: () => {
    const { currentBgm } = get();
    if (!currentBgm) return;
    currentBgm.pause();
    set({ isCurrentlyPlaying: false });
  },

  resume: () => {
    const { currentBgm, cancelFadeOut } = get();
    cancelFadeOut();
    if (!currentBgm) {
      return;
    }
    if (currentBgm.playing()) {
      return;
    }
    currentBgm.play();
    set({ isCurrentlyPlaying: true });
  },

  setVolume: (volume: number) => {
    const { currentBgm } = get();
    localStorage.setItem('bgmVolume', `${volume}`);
    set({ volume });
    currentBgm?.volume(calculateVolume(volume));
  },

  toggleBgm: () => {
    const { isBgmEnabled, pause, play } = get();
    const newIsBgmEnabled = !isBgmEnabled;
    localStorage.setItem('isBgmEnabled', `${newIsBgmEnabled}`);
    set({ isBgmEnabled: newIsBgmEnabled });
    if (!newIsBgmEnabled) {
      pause();
      return false;
    }
    play();
    return true;
  },

  toggleTrack: (title: string) => {
    const { checkedTitles } = get();
    const next = checkedTitles.includes(title)
      ? checkedTitles.filter((t) => t !== title)
      : [...checkedTitles, title];
    localStorage.setItem('bgmCheckedTitles', JSON.stringify(next));
    set({ checkedTitles: next });
  },

  setCheckedTitles: (titles: string[]) => {
    localStorage.setItem('bgmCheckedTitles', JSON.stringify(titles));
    set({ checkedTitles: titles });
  },

  applyChecklist: () => {
    const { checkedTitles, currentBgm, isInitialized, isBgmEnabled, play } =
      get();
    const newPlaylist = buildPlaylist(checkedTitles);
    currentBgm?.unload();
    set({ playlist: newPlaylist, currentIndex: 0, currentBgm: null });
    if (isInitialized && isBgmEnabled) {
      play();
    }
  },

  cancelFadeOut: () => {
    const { currentBgm, volume } = get();
    if (currentBgm) {
      currentBgm.volume(calculateVolume(volume));
    }
  },

  fadeIn: (duration: number) => {
    const { currentBgm, volume, cancelFadeOut } = get();
    cancelFadeOut();
    if (!currentBgm) return;
    currentBgm.volume(0);
    currentBgm.play();
    currentBgm.fade(0, calculateVolume(volume), duration);
    set({ isCurrentlyPlaying: true });
  },

  isPlaying: () => {
    const { currentBgm } = get();
    if (!currentBgm) {
      return false;
    }
    return currentBgm.playing();
  },

  destroy: () => {
    const { currentBgm } = get();
    if (!currentBgm) {
      return;
    }
    currentBgm.unload();
    set({ currentBgm: null, isCurrentlyPlaying: false });
  },
}));
